import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from 'antd';
import FileSaver from 'file-saver';
import {
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import EnvironmentSelect from '@lib/components/env-select';

import {
  cancelScan,
  downloadScanPlayersResult,
  getScanHistory,
  getScanStatus,
  getScanTaskStatus,
  queryResource,
  scanAllPlayers,
  ScanHistoryItemDto,
  scanPlayers,
} from '@services/cstool/scan-players';

const { Text, Title } = Typography;

type ScanOutputFormat = 'txt' | 'json';

type ScanAllSummary = {
  totalCount: number;
  fileName: string;
  outputFormat: ScanOutputFormat;
  previewPlayerIds: string[];
  previewRecords?: Record<string, any>[];
};

// Both scan flavors now run as background task queue jobs, so their result shape only
// ever surfaces through TaskQueueResponseDto.returnValue (typed as a generic object) -
// these mirror the backend DTOs by hand since swagger has no visibility into that shape.
type ScanResult = {
  playerIds: string[];
  lastEvaluatedKey: Record<string, any> | null;
  isTruncated: boolean;
  scannedCount: number;
  count: number;
  outputFormat: ScanOutputFormat;
  records?: Record<string, any>[];
};

type JobProgress = {
  scannedCount: number;
  matchedCount: number;
};

// Quick Filter lets a user build a DynamoDB FilterExpression without hand-writing raw
// expression syntax or ExpressionAttributeValues/Names JSON - each row is one
// `attribute operator value` condition, and every filled-in row is AND-joined into the
// three raw fields below (see buildQuickFilter), which stay directly editable for anything
// this simple builder can't express (nested boolean logic, contains/exists, etc).
type FilterOperator = '=' | '<>' | '<' | '<=' | '>' | '>=';

type FilterRow = {
  // Local-only React key - never sent to the backend.
  id: string;
  attribute: string;
  operator: FilterOperator;
  value: string;
};

const FILTER_OPERATOR_OPTIONS: { value: FilterOperator; label: string }[] = [
  { value: '=', label: '==' },
  { value: '<>', label: '!=' },
  { value: '<', label: '<' },
  { value: '<=', label: '<=' },
  { value: '>', label: '>' },
  { value: '>=', label: '>=' },
];

const createEmptyFilterRow = (): FilterRow => ({
  id: Math.random().toString(36).slice(2),
  attribute: '',
  operator: '=',
  value: '',
});

// Mirrors the raw typed AttributeValue format the raw "Expression Attribute Values" field
// already expects (see ScanPlayersDto.expressionAttributeValues) - true/false and numbers
// are coerced so e.g. Level > 50 compares numerically instead of lexically as a string.
const buildAttributeValue = (raw: string): Record<string, any> => {
  const trimmed = raw.trim();
  if (/^(true|false)$/i.test(trimmed)) return { BOOL: trimmed.toLowerCase() === 'true' };
  if (trimmed !== '' && !Number.isNaN(Number(trimmed))) return { N: trimmed };
  return { S: raw };
};

const buildQuickFilter = (
  rows: FilterRow[],
): { filterExpression: string; values: Record<string, any>; names: Record<string, string> } => {
  const validRows = rows.filter((row) => row.attribute.trim() && row.value.trim());

  const names: Record<string, string> = {};
  const values: Record<string, any> = {};
  const clauses = validRows.map((row, index) => {
    // Always aliased through ExpressionAttributeNames (rather than used literally), since a
    // user-entered attribute name might collide with a DynamoDB reserved word.
    const nameAlias = row.attribute
      .split('.')
      .map((segment, segmentIndex) => {
        const alias = `#qf${index}_${segmentIndex}`;
        names[alias] = segment.trim();
        return alias;
      })
      .join('.');
    const valueAlias = `:qf${index}`;
    values[valueAlias] = buildAttributeValue(row.value);
    return `${nameAlias} ${row.operator} ${valueAlias}`;
  });

  return { filterExpression: clauses.join(' AND '), values, names };
};

const parseJsonField = (raw: string, fieldName: string): Record<string, any> | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    throw new Error(`${fieldName} is not valid JSON: ${(error as Error).message}`);
  }
};

// Comma-separated extra field names for JSON output, e.g. "HighestDivisionId, Level".
const parseFieldList = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean),
    ),
  );

const idOnlyColumns = [{ title: 'Id', dataIndex: 'Id', key: 'Id' }];

// Derived from the records themselves (Id + whichever jsonFields the backend actually found on
// each item) rather than the live "JSON fields" input, so columns stay correct for a job that
// already finished even if the input has since been edited for the next run.
const buildRecordColumns = (records: Record<string, any>[]) => {
  if (!records.length) return idOnlyColumns;
  return Object.keys(records[0]).map((key) => ({ title: key, dataIndex: key, key }));
};

type QueryResourceResult = {
  table: string;
  fileName: string;
  scannedCount: number;
  count: number;
  outputFormat: 'json';
  previewPlayerIds: string[];
  previewRecords: Record<string, any>[];
};

// Same idea as buildRecordColumns, but unions keys across every record instead of reading only
// the first one - a Query Resource record only carries the currency key when a match was
// found for that player, so a player with no match happening to be first would otherwise hide
// the currency column for every other row.
const buildUnionRecordColumns = (records: Record<string, any>[]) => {
  if (!records.length) return idOnlyColumns;
  const keys = new Set<string>();
  records.forEach((record) => Object.keys(record).forEach((key) => keys.add(key)));
  return [...keys].map((key) => ({ title: key, dataIndex: key, key }));
};

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsText(file);
  });

// Txt: one id per line, or a comma/semicolon-separated list (mirrors Inbox Batch Players'
// parsing). Json: an array of items, each read by its "id" field - checked case-insensitively
// (also "Id"/"PlayerId") so a JSON file straight off the Scan tab's own Download button (whose
// records use "Id") can be fed back in here unmodified.
const parsePlayerIdsFromFile = async (file: File): Promise<string[]> => {
  const text = await readFileAsText(file);

  if (file.name.toLowerCase().endsWith('.json')) {
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      throw new Error(`Uploaded file is not valid JSON: ${(error as Error).message}`);
    }
    if (!Array.isArray(parsed)) {
      throw new Error('Uploaded JSON file must contain an array of items');
    }
    return parsed
      .map((item) =>
        item && typeof item === 'object' ? item.id ?? item.Id ?? item.PlayerId : undefined,
      )
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim());
  }

  return text
    .split(/[\r\n,;]+/)
    .map((id) => id.trim())
    .filter(Boolean);
};

const POLL_INTERVAL_MS = 5000;
const PROGRESS_POLL_INTERVAL_MS = 3000;
const HISTORY_PAGE_SIZE = 20;
// Matches the backend's own default (QueryResourceService.doQueryResource) - shown pre-filled
// in the Table field below rather than left blank, since that's what querying used to always do
// before the table became overridable.
const DEFAULT_RESOURCE_TABLE_NAME = 'Resources';
// A bounded scan/load-more job can match up to 10,000 player ids (see ScanPlayersDto.limit) -
// rendering all of them inline in an expanded row would be an unbounded wall of text, so only
// a preview is shown here; Download always gets the full list regardless of this cap.
const HISTORY_PLAYER_ID_PREVIEW_SIZE = 100;

const HISTORY_KIND_LABELS: Record<ScanHistoryItemDto['kind'], string> = {
  scan: 'Scan',
  'load-more': 'Load more',
  'scan-all': 'Full scan',
  'query-resource': 'Query Resource',
};

const HISTORY_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  DEPLOYING: 'Running',
  SUCCESS: 'Success',
  FAIL: 'Failed',
  TIMEOUT: 'Timed out',
  UNKNOWN: 'Unknown',
};

const HISTORY_STATUS_COLORS: Record<string, string> = {
  PENDING: 'default',
  DEPLOYING: 'processing',
  SUCCESS: 'success',
  FAIL: 'error',
  TIMEOUT: 'warning',
  UNKNOWN: 'default',
};

const canDownloadHistoryItem = (item: ScanHistoryItemDto) =>
  item.status === 'SUCCESS' && (Boolean(item.fileName) || Boolean(item.playerIds?.length));

// The generated client types `params` as a bare `object` (all OpenAPI can say for an
// arbitrary JSON blob) - this is the one place that gets treated as the request body shape it
// actually is, so a codegen refresh can't silently break property access at every call site.
const getHistoryParams = (item: ScanHistoryItemDto): Record<string, any> | null =>
  (item.params as Record<string, any> | null) ?? null;

// Params saved for a query-resource job have no outputFormat field (it's always json) -
// unlike scan/scan-all, whose outputFormat is whatever the user picked on the Scan tab.
const getHistoryOutputFormat = (item: ScanHistoryItemDto): ScanOutputFormat =>
  item.kind === 'query-resource'
    ? 'json'
    : (getHistoryParams(item)?.outputFormat as ScanOutputFormat | undefined) ?? 'txt';

const formatHistoryTimestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '-';

// Only one scan job can ever be running on the backend at a time, so remembering just the
// last one's taskId/kind is enough to resume the status view after a reload - reload
// otherwise loses all local state even though the backend job keeps running unattended.
const ACTIVE_JOB_STORAGE_KEY = 'cstool:scan-players:active-job';

type ActiveJobKind = 'scan' | 'load-more' | 'scan-all' | 'query-resource';

type StoredActiveJob = { taskId: number; kind: ActiveJobKind };

const readStoredActiveJob = (): StoredActiveJob | null => {
  try {
    const raw = localStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.taskId === 'number' ? (parsed as StoredActiveJob) : null;
  } catch {
    return null;
  }
};

const writeStoredActiveJob = (job: StoredActiveJob) => {
  try {
    localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, JSON.stringify(job));
  } catch {
    // best-effort only - if storage is unavailable, reload just won't be able to resume
  }
};

const clearStoredActiveJob = () => {
  try {
    localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
  } catch {
    // best-effort only
  }
};

const ScanPlayersScreen: FC = () => {
  const [environment, setEnvironment] = useState<string>();
  const [quickFilterRows, setQuickFilterRows] = useState<FilterRow[]>([createEmptyFilterRow()]);
  const [filterExpression, setFilterExpression] = useState('');
  const [attributeValuesRaw, setAttributeValuesRaw] = useState('');
  const [attributeNamesRaw, setAttributeNamesRaw] = useState('');
  const [limit, setLimit] = useState<number | undefined>();
  const [limitMatch, setLimitMatch] = useState<number | undefined>();
  const [scanAll, setScanAll] = useState(false);
  const [outputFormat, setOutputFormat] = useState<ScanOutputFormat>('txt');
  const [jsonFieldsRaw, setJsonFieldsRaw] = useState('');
  const jsonFields = parseFieldList(jsonFieldsRaw);
  const [segments, setSegments] = useState<number | undefined>();

  const [playerIds, setPlayerIds] = useState<string[]>([]);
  const [matchedRecords, setMatchedRecords] = useState<Record<string, any>[]>([]);
  const [resultOutputFormat, setResultOutputFormat] = useState<ScanOutputFormat>('txt');
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Record<string, any> | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [scannedCount, setScannedCount] = useState(0);
  const [matchedCount, setMatchedCount] = useState(0);

  const [scanAllSummary, setScanAllSummary] = useState<ScanAllSummary | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Every scan flavor (bounded scan, "load more", scan-all, query-resource) now runs as a
  // background task queue job - the backend only ever runs one at a time, so a single shared
  // taskId/progress/cancel setup covers all of them; these booleans just track which one is
  // currently in flight so the right button shows a spinner.
  const [isScanning, setIsScanning] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isScanningAll, setIsScanningAll] = useState(false);
  const [isQueryingResource, setIsQueryingResource] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const [historyItems, setHistoryItems] = useState<ScanHistoryItemDto[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [downloadingHistoryId, setDownloadingHistoryId] = useState<number | null>(null);

  const [qrEnvironment, setQrEnvironment] = useState<string>();
  const [qrTable, setQrTable] = useState(DEFAULT_RESOURCE_TABLE_NAME);
  const [qrCurrencyId, setQrCurrencyId] = useState('');
  const [qrPlayerIds, setQrPlayerIds] = useState<string[]>([]);
  const [qrFileName, setQrFileName] = useState<string>();
  const [qrPreviewRecords, setQrPreviewRecords] = useState<Record<string, any>[]>([]);
  const [qrSummary, setQrSummary] = useState<{
    table: string;
    resultFileName: string;
    scannedCount: number;
    count: number;
  } | null>(null);

  const isJobRunning = isScanning || isLoadingMore || isScanningAll || isQueryingResource;
  const isFormDisabled = isJobRunning;
  const canSubmit = Boolean(environment) && !isFormDisabled;
  const canSubmitQueryResource =
    Boolean(qrEnvironment) &&
    Boolean(qrTable.trim()) &&
    Boolean(qrCurrencyId.trim()) &&
    qrPlayerIds.length > 0 &&
    !isFormDisabled;

  // Autofills the raw Filter Expression / Attribute Values / Attribute Names fields from
  // Quick Filter any time a row changes - those raw fields remain directly editable
  // afterward for anything Quick Filter can't express, but a further Quick Filter edit will
  // regenerate and overwrite them again.
  useEffect(() => {
    const built = buildQuickFilter(quickFilterRows);
    setFilterExpression(built.filterExpression);
    setAttributeValuesRaw(built.filterExpression ? JSON.stringify(built.values, null, 2) : '');
    setAttributeNamesRaw(built.filterExpression ? JSON.stringify(built.names, null, 2) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickFilterRows]);

  const handleAddFilterRow = () => {
    setQuickFilterRows((rows) => [...rows, createEmptyFilterRow()]);
  };

  const handleRemoveFilterRow = (id: string) => {
    setQuickFilterRows((rows) => (rows.length > 1 ? rows.filter((row) => row.id !== id) : rows));
  };

  const handleFilterRowChange = (id: string, patch: Partial<FilterRow>) => {
    setQuickFilterRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const parseJsonFields = (): {
    values?: Record<string, any>;
    names?: Record<string, any>;
  } | null => {
    try {
      return {
        values: parseJsonField(attributeValuesRaw, 'Expression Attribute Values'),
        names: parseJsonField(attributeNamesRaw, 'Expression Attribute Names'),
      };
    } catch (error) {
      message.error((error as Error).message);
      return null;
    }
  };

  const pollJobProgress = () => {
    getScanStatus().then((res) => {
      const data = res.data as any;
      if (!res.ok || !data?.running) return;

      setJobProgress({
        scannedCount: data.scannedCount ?? 0,
        matchedCount: data.matchedCount ?? 0,
      });
      setTimeout(pollJobProgress, PROGRESS_POLL_INTERVAL_MS);
    });
  };

  const pollTaskStatus = (
    taskId: number,
    onSuccess: (returnValue: any) => void,
    onDone: () => void,
  ) => {
    getScanTaskStatus(taskId).then((res) => {
      if (!res.ok) {
        message.error('Failed to check scan status');
        onDone();
        return;
      }

      if (res.data?.status === 'SUCCESS') {
        onSuccess(res.data?.returnValue);
        onDone();
      } else if (res.data?.status === 'DEPLOYING') {
        setTimeout(() => pollTaskStatus(taskId, onSuccess, onDone), POLL_INTERVAL_MS);
      } else {
        const failureMessage =
          typeof res.data?.returnValue === 'string' ? res.data.returnValue : 'Scan failed';
        message.error(failureMessage);
        onDone();
      }
    });
  };

  const runScan = async (exclusiveStartKey?: Record<string, any>) => {
    if (!environment) return;

    const parsed = parseJsonFields();
    if (!parsed) return;

    const setLoading = exclusiveStartKey ? setIsLoadingMore : setIsScanning;
    setLoading(true);
    setJobProgress(null);

    // client.ts's response interceptor already toasts the server's error message for any
    // non-2xx response - it then re-throws, so this must be caught or the loading flag
    // above is never cleared and the form stays stuck disabled.
    let response;
    try {
      response = await scanPlayers({
        environment,
        filterExpression: filterExpression.trim() || undefined,
        expressionAttributeValues: parsed.values,
        expressionAttributeNames: parsed.names,
        exclusiveStartKey,
        limit,
        limitMatch,
        outputFormat,
        jsonFields: outputFormat === 'json' ? jsonFields : undefined,
      });
    } catch {
      setLoading(false);
      return;
    }

    if (!response.data?.id) {
      message.error('Failed to start scan');
      setLoading(false);
      return;
    }

    setActiveTaskId(response.data.id);
    writeStoredActiveJob({
      taskId: response.data.id,
      kind: exclusiveStartKey ? 'load-more' : 'scan',
    });
    pollJobProgress();
    pollTaskStatus(
      response.data.id,
      (returnValue) => {
        // The job resolves with { params, result } (see scanPlayers() on the backend, added
        // for the History tab) - unwrap .result rather than treating the whole envelope as
        // the flat ScanResult shape, or every field below reads undefined.
        const result = returnValue?.result as ScanResult;
        setResultOutputFormat(result.outputFormat ?? 'txt');
        setPlayerIds((current) =>
          exclusiveStartKey ? [...current, ...result.playerIds] : result.playerIds,
        );
        setMatchedRecords((current) =>
          exclusiveStartKey ? [...current, ...(result.records ?? [])] : result.records ?? [],
        );
        setLastEvaluatedKey(result.lastEvaluatedKey ?? null);
        setIsTruncated(result.isTruncated);
        setScannedCount((current) =>
          exclusiveStartKey ? current + result.scannedCount : result.scannedCount,
        );
        setMatchedCount((current) => (exclusiveStartKey ? current + result.count : result.count));
      },
      () => {
        setLoading(false);
        setJobProgress(null);
        clearStoredActiveJob();
      },
    );
  };

  const runScanAll = async () => {
    if (!environment) return;

    const parsed = parseJsonFields();
    if (!parsed) return;

    setScanAllSummary(null);
    setJobProgress(null);
    setIsScanningAll(true);

    let response;
    try {
      response = await scanAllPlayers({
        environment,
        filterExpression: filterExpression.trim() || undefined,
        expressionAttributeValues: parsed.values,
        expressionAttributeNames: parsed.names,
        outputFormat,
        jsonFields: outputFormat === 'json' ? jsonFields : undefined,
        segments,
      });
    } catch {
      setIsScanningAll(false);
      return;
    }

    if (!response.data?.id) {
      message.error('Failed to start scan');
      setIsScanningAll(false);
      return;
    }

    setActiveTaskId(response.data.id);
    writeStoredActiveJob({ taskId: response.data.id, kind: 'scan-all' });
    pollJobProgress();
    pollTaskStatus(
      response.data.id,
      // Same { params, result } envelope as runScan() above - unwrap .result.
      (returnValue) => setScanAllSummary(returnValue?.result as ScanAllSummary),
      () => {
        setIsScanningAll(false);
        setJobProgress(null);
        clearStoredActiveJob();
      },
    );
  };

  const handleQueryResourceFileSelect = async (file: File) => {
    try {
      const ids = [...new Set(await parsePlayerIdsFromFile(file))];
      if (!ids.length) {
        message.error('No player ids found in the uploaded file');
        return false;
      }
      setQrPlayerIds(ids);
      setQrFileName(file.name);
      message.success(`Loaded ${ids.length} unique player id(s) from ${file.name}`);
    } catch (error) {
      message.error((error as Error).message);
    }
    // Always false - this only reads the file client-side to build playerIds, it should never
    // let antd upload it anywhere.
    return false;
  };

  const handleRemoveQueryResourceFile = () => {
    setQrPlayerIds([]);
    setQrFileName(undefined);
  };

  const runQueryResource = async () => {
    if (!qrEnvironment || !qrTable.trim() || !qrCurrencyId.trim() || !qrPlayerIds.length) return;

    setQrPreviewRecords([]);
    setQrSummary(null);
    setJobProgress(null);
    setIsQueryingResource(true);

    let response;
    try {
      response = await queryResource({
        environment: qrEnvironment,
        table: qrTable.trim(),
        currencyId: qrCurrencyId.trim(),
        playerIds: qrPlayerIds,
      });
    } catch {
      setIsQueryingResource(false);
      return;
    }

    if (!response.data?.id) {
      message.error('Failed to start query');
      setIsQueryingResource(false);
      return;
    }

    setActiveTaskId(response.data.id);
    writeStoredActiveJob({ taskId: response.data.id, kind: 'query-resource' });
    pollJobProgress();
    pollTaskStatus(
      response.data.id,
      // Same { params, result } envelope as runScan()/runScanAll() above - unwrap .result.
      (returnValue) => {
        const result = returnValue?.result as QueryResourceResult;
        setQrPreviewRecords(result.previewRecords ?? []);
        setQrSummary({
          table: result.table ?? qrTable,
          resultFileName: result.fileName,
          scannedCount: result.scannedCount,
          count: result.count,
        });
      },
      () => {
        setIsQueryingResource(false);
        setJobProgress(null);
        clearStoredActiveJob();
      },
    );
  };

  // Resume the status view for whichever scan is actually running, if any - otherwise a page
  // reload mid-scan loses all progress/cancel UI even though the backend job (which has no
  // idea the page reloaded) keeps running to completion regardless. GET /status now reports
  // the real job (backed by the TaskQueue row, not just this browser's own memory), so this
  // resumes correctly even in a fresh browser/incognito window or a different admin's session -
  // localStorage is only a same-browser fast path/fallback, not the source of truth.
  useEffect(() => {
    const stored = readStoredActiveJob();

    getScanStatus().then((res) => {
      const data = res.data as any;
      const running = Boolean(res.ok && data?.running);
      const taskId: number | undefined = running ? data?.taskId ?? stored?.taskId : stored?.taskId;
      if (!taskId) return;

      const kind: ActiveJobKind = (running ? data?.kind : stored?.kind) ?? 'scan';
      let setLoading = setIsScanning;
      if (kind === 'scan-all') setLoading = setIsScanningAll;
      else if (kind === 'load-more') setLoading = setIsLoadingMore;
      else if (kind === 'query-resource') setLoading = setIsQueryingResource;

      setLoading(true);
      setActiveTaskId(taskId);

      if (running) {
        setJobProgress({
          scannedCount: data.scannedCount ?? 0,
          matchedCount: data.matchedCount ?? 0,
        });
        pollJobProgress();
      }

      pollTaskStatus(
        taskId,
        (returnValue) => {
          // Same { params, result } envelope as runScan()/runScanAll()/runQueryResource() above.
          if (kind === 'scan-all') {
            setScanAllSummary(returnValue?.result as ScanAllSummary);
          } else if (kind === 'query-resource') {
            const result = returnValue?.result as QueryResourceResult;
            setQrPreviewRecords(result.previewRecords ?? []);
            setQrSummary({
              table: result.table ?? DEFAULT_RESOURCE_TABLE_NAME,
              resultFileName: result.fileName,
              scannedCount: result.scannedCount,
              count: result.count,
            });
          } else {
            const result = returnValue?.result as ScanResult;
            setResultOutputFormat(result.outputFormat ?? 'txt');
            setPlayerIds(result.playerIds);
            setMatchedRecords(result.records ?? []);
            setLastEvaluatedKey(result.lastEvaluatedKey ?? null);
            setIsTruncated(result.isTruncated);
            setScannedCount(result.scannedCount);
            setMatchedCount(result.count);
          }
        },
        () => {
          setLoading(false);
          setJobProgress(null);
          clearStoredActiveJob();
        },
      );
    });
    // Runs once on mount only - resuming an in-flight job is a one-time reload recovery,
    // not something that should re-fire as the polling helpers above are recreated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = async (page: number) => {
    setHistoryLoading(true);
    try {
      const res = await getScanHistory((page - 1) * HISTORY_PAGE_SIZE, HISTORY_PAGE_SIZE);
      if (res.ok && res.data) {
        setHistoryItems(res.data.data);
        setHistoryTotal(res.data.total);
        setHistoryPage(page);
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryTabChange = (key: string) => {
    if (key === 'history') fetchHistory(1);
  };

  const handleDownloadHistoryItem = async (item: ScanHistoryItemDto) => {
    setDownloadingHistoryId(item.id);
    const format = getHistoryOutputFormat(item);
    try {
      if (item.fileName) {
        await downloadScanPlayersResult(item.fileName, format);
      } else if (format === 'json' && item.records?.length) {
        const blob = new Blob([JSON.stringify(item.records, null, 2)], {
          type: 'application/json;charset=utf-8',
        });
        FileSaver.saveAs(blob, `scan-players-history-${item.id}.json`);
      } else if (item.playerIds?.length) {
        const blob = new Blob([item.playerIds.join(',')], { type: 'text/plain;charset=utf-8' });
        FileSaver.saveAs(blob, `scan-players-history-${item.id}.txt`);
      }
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setDownloadingHistoryId(null);
    }
  };

  const historyColumns = [
    {
      title: 'Kind',
      dataIndex: 'kind',
      key: 'kind',
      render: (kind: ScanHistoryItemDto['kind']) => HISTORY_KIND_LABELS[kind] ?? kind,
    },
    {
      title: 'Environment',
      key: 'environment',
      render: (_: unknown, item: ScanHistoryItemDto) => getHistoryParams(item)?.environment ?? '-',
    },
    {
      title: 'Filter Expression',
      key: 'filterExpression',
      render: (_: unknown, item: ScanHistoryItemDto) =>
        getHistoryParams(item)?.filterExpression || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={HISTORY_STATUS_COLORS[status] ?? 'default'}>
          {HISTORY_STATUS_LABELS[status] ?? status}
        </Tag>
      ),
    },
    {
      title: 'Scanned / Matched',
      key: 'counts',
      render: (_: unknown, item: ScanHistoryItemDto) =>
        `${item.scannedCount ?? '-'} / ${item.matchedCount ?? '-'}`,
    },
    {
      title: 'Started',
      dataIndex: 'startTime',
      key: 'startTime',
      render: formatHistoryTimestamp,
    },
    {
      title: 'Ended',
      dataIndex: 'endTime',
      key: 'endTime',
      render: formatHistoryTimestamp,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, item: ScanHistoryItemDto) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          disabled={!canDownloadHistoryItem(item)}
          loading={downloadingHistoryId === item.id}
          onClick={() => handleDownloadHistoryItem(item)}
        >
          Download
        </Button>
      ),
    },
  ];

  const handleScan = () => {
    if (scanAll) {
      runScanAll();
    } else {
      runScan();
    }
  };
  const handleLoadMore = () => {
    if (lastEvaluatedKey) runScan(lastEvaluatedKey);
  };
  const handleDownload = () => {
    if (resultOutputFormat === 'json') {
      const blob = new Blob([JSON.stringify(matchedRecords, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      FileSaver.saveAs(blob, `scan-players-${environment}.json`);
    } else {
      const blob = new Blob([playerIds.join(',')], { type: 'text/plain;charset=utf-8' });
      FileSaver.saveAs(blob, `scan-players-${environment}.txt`);
    }
  };
  const handleDownloadScanAllResult = async () => {
    if (!scanAllSummary) return;
    setIsDownloading(true);
    try {
      await downloadScanPlayersResult(scanAllSummary.fileName, scanAllSummary.outputFormat);
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setIsDownloading(false);
    }
  };
  const handleCancelScan = async () => {
    setIsCancelling(true);
    try {
      const res = await cancelScan();
      if (!res.ok) {
        message.error((res.error as any)?.message ?? 'Failed to cancel scan');
        return;
      }
      message.info('Cancelling - this may take a moment to stop.');
    } finally {
      setIsCancelling(false);
    }
  };
  const handleDownloadQueryResource = async () => {
    if (!qrSummary?.resultFileName) return;
    setIsDownloading(true);
    try {
      await downloadScanPlayersResult(qrSummary.resultFileName, 'json');
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setIsDownloading(false);
    }
  };

  const dataSource =
    resultOutputFormat === 'json' ? matchedRecords : playerIds.map((id) => ({ Id: id }));
  const resultColumns =
    resultOutputFormat === 'json' ? buildRecordColumns(matchedRecords) : idOnlyColumns;

  const previewDataSource =
    scanAllSummary?.outputFormat === 'json'
      ? scanAllSummary?.previewRecords ?? []
      : (scanAllSummary?.previewPlayerIds ?? []).map((id) => ({ Id: id }));
  const previewColumns =
    scanAllSummary?.outputFormat === 'json'
      ? buildRecordColumns(scanAllSummary?.previewRecords ?? [])
      : idOnlyColumns;

  const qrColumns = buildUnionRecordColumns(qrPreviewRecords);

  let activeJobLabel = 'Scan';
  if (isScanningAll) activeJobLabel = 'Full scan';
  else if (isLoadingMore) activeJobLabel = 'Load more';
  else if (isQueryingResource) activeJobLabel = 'Query Resource';

  const jobRunningAlert = isJobRunning && (
    <Alert
      className="mb-4"
      type="info"
      showIcon
      icon={<Spin size="small" />}
      message={
        `${activeJobLabel} running${activeTaskId ? ` (job #${activeTaskId})` : ''} - ` +
        `scanned ${jobProgress?.scannedCount ?? 0} item(s), matched ${
          jobProgress?.matchedCount ?? 0
        } so far.`
      }
      description="This can take a while for a large table. You can keep using other tabs while it runs."
      action={(
        <Button
          danger
          size="small"
          icon={<StopOutlined />}
          loading={isCancelling}
          onClick={handleCancelScan}
        >
          Cancel scan
        </Button>
      )}
    />
  );

  return (
    <div className="flex flex-col flex-1 max-h-full min-h-full p-4">
      <Tabs defaultActiveKey="scan" onChange={handleHistoryTabChange}>
        <Tabs.TabPane tab="Scan" key="scan">
          <Row gutter={[16, 16]} className="mb-4">
            <Col>
              <Title level={5}>Environment</Title>
              <EnvironmentSelect
                value={environment}
                style={{ width: 200 }}
                onChange={setEnvironment}
                disabled={isFormDisabled}
              />
            </Col>
            <Col>
              <Title level={5}>Limit</Title>
              <InputNumber
                min={1}
                max={10000}
                value={limit}
                placeholder="200"
                onChange={(value) => setLimit(typeof value === 'number' ? value : undefined)}
                style={{ width: 200 }}
                disabled={isFormDisabled || scanAll || Boolean(limitMatch)}
              />
            </Col>
            <Col>
              <Title level={5}>Limit match</Title>
              <InputNumber
                min={1}
                max={1000000}
                value={limitMatch}
                placeholder="e.g. 500"
                onChange={(value) => setLimitMatch(typeof value === 'number' ? value : undefined)}
                style={{ width: 200 }}
                disabled={isFormDisabled || scanAll || Boolean(limit)}
              />
            </Col>
            <Col className="flex flex-col justify-end">
              <Checkbox
                checked={scanAll}
                disabled={isFormDisabled}
                onChange={(e) => setScanAll(e.target.checked)}
              >
                Scan all (no limit)
              </Checkbox>
            </Col>
            <Col>
              <Title level={5}>Segments</Title>
              <InputNumber
                min={1}
                max={50}
                value={segments}
                placeholder="1 (sequential)"
                onChange={(value) => setSegments(typeof value === 'number' ? value : undefined)}
                style={{ width: 200 }}
                disabled={isFormDisabled || !scanAll}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="mb-4">
            <Col>
              <Title level={5}>Output</Title>
              <Radio.Group
                value={outputFormat}
                disabled={isFormDisabled}
                onChange={(e) => setOutputFormat(e.target.value)}
              >
                <Radio.Button value="txt">Txt (id only)</Radio.Button>
                <Radio.Button value="json">Json</Radio.Button>
              </Radio.Group>
            </Col>
            {outputFormat === 'json' && (
              <Col flex="auto">
                <Title level={5}>Json Fields</Title>
                <Input
                  value={jsonFieldsRaw}
                  placeholder="Comma-separated extra fields to include, e.g. HighestDivisionId, Level"
                  onChange={(e) => setJsonFieldsRaw(e.target.value)}
                  disabled={isFormDisabled}
                />
              </Col>
            )}
          </Row>
          <Row gutter={[16, 16]} className="mb-4">
            <Col flex="auto">
              <Title level={5}>Quick Filter</Title>
              <div className="flex flex-col gap-2">
                {quickFilterRows.map((row) => (
                  <div key={row.id} className="flex gap-2">
                    <Input
                      placeholder="Attribute name, e.g. Data or Data.Info"
                      value={row.attribute}
                      onChange={(e) => handleFilterRowChange(row.id, { attribute: e.target.value })}
                      disabled={isFormDisabled}
                      style={{ maxWidth: 280 }}
                    />
                    <Select
                      value={row.operator}
                      onChange={(value) => handleFilterRowChange(row.id, { operator: value })}
                      disabled={isFormDisabled}
                      style={{ width: 90 }}
                      options={FILTER_OPERATOR_OPTIONS}
                    />
                    <Input
                      placeholder="Value, e.g. Info"
                      value={row.value}
                      onChange={(e) => handleFilterRowChange(row.id, { value: e.target.value })}
                      disabled={isFormDisabled}
                      style={{ maxWidth: 280 }}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      disabled={isFormDisabled || quickFilterRows.length === 1}
                      onClick={() => handleRemoveFilterRow(row.id)}
                    />
                  </div>
                ))}
                <Button
                  icon={<PlusOutlined />}
                  disabled={isFormDisabled}
                  onClick={handleAddFilterRow}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add condition
                </Button>
              </div>
              <Text type="secondary">
                Conditions are AND-joined and fill in Filter Expression / Attribute Values / Names
                below - edit those directly for anything Quick Filter can&apos;t express.
              </Text>
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="mb-4">
            <Col flex="auto">
              <Title level={5}>Filter Expression</Title>
              <Input.TextArea
                rows={2}
                value={filterExpression}
                placeholder="PlayerLevel.LastClaimedPlayerLevelReward >= :lo AND PlayerLevel.LastClaimedPlayerLevelReward < :hi"
                onChange={(e) => setFilterExpression(e.target.value)}
                disabled={isFormDisabled}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="mb-4">
            <Col flex="1 1 50%">
              <Title level={5}>Expression Attribute Values</Title>
              <Input.TextArea
                rows={4}
                value={attributeValuesRaw}
                placeholder={'{":lo":{"N":"50"},":hi":{"N":"100"}}'}
                onChange={(e) => setAttributeValuesRaw(e.target.value)}
                disabled={isFormDisabled}
              />
            </Col>
            <Col flex="1 1 50%">
              <Title level={5}>Expression Attribute Names (optional)</Title>
              <Input.TextArea
                rows={4}
                value={attributeNamesRaw}
                placeholder={'{"#lvl":"Level"}'}
                onChange={(e) => setAttributeNamesRaw(e.target.value)}
                disabled={isFormDisabled}
              />
            </Col>
          </Row>
          <Row gutter={16} className="mb-4">
            <Col>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={isScanning || isScanningAll}
                disabled={!canSubmit}
                onClick={handleScan}
              >
                {scanAll ? 'Start full scan' : 'Scan'}
              </Button>
            </Col>
          </Row>

          {jobRunningAlert}

          {!scanAll && (playerIds.length > 0 || scannedCount > 0) && (
            <>
              <Alert
                className="mb-2"
                type="info"
                showIcon
                message={`Scanned ${scannedCount} item(s), matched ${matchedCount}${
                  isTruncated ? ' - more results may be available' : ''
                }`}
                action={(
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    disabled={isJobRunning}
                    onClick={handleDownload}
                  >
                    Download .{resultOutputFormat}
                  </Button>
                )}
              />
              <Table
                rowKey="Id"
                columns={resultColumns}
                dataSource={dataSource}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true }}
              />
              {isTruncated && (
                <div className="mt-2 flex justify-center">
                  <Button loading={isLoadingMore} disabled={isScanning} onClick={handleLoadMore}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}

          {scanAll && scanAllSummary && (
            <>
              <Alert
                className="mb-2"
                type="success"
                showIcon
                message={`Matched ${scanAllSummary.totalCount} distinct player(s) across the whole table.`}
                action={(
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={isDownloading}
                    onClick={handleDownloadScanAllResult}
                  >
                    Download full result (.{scanAllSummary.outputFormat})
                  </Button>
                )}
              />
              <Text type="secondary">
                Showing a preview of the first {previewDataSource.length} matched player id(s) -
                download the file above for the full list.
              </Text>
              <Table
                className="mt-2"
                rowKey="Id"
                columns={previewColumns}
                dataSource={previewDataSource}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true }}
              />
            </>
          )}
        </Tabs.TabPane>

        <Tabs.TabPane tab="Query Resource" key="query-resource">
          <Row gutter={[16, 16]} className="mb-4">
            <Col>
              <Title level={5}>Environment</Title>
              <EnvironmentSelect
                value={qrEnvironment}
                style={{ width: 200 }}
                onChange={setQrEnvironment}
                disabled={isFormDisabled}
              />
            </Col>
            <Col>
              <Title level={5}>Table</Title>
              <Input
                value={qrTable}
                placeholder={DEFAULT_RESOURCE_TABLE_NAME}
                onChange={(e) => setQrTable(e.target.value)}
                style={{ width: 200 }}
                disabled={isFormDisabled}
              />
            </Col>
            <Col>
              <Title level={5}>Currency Id (ItemId)</Title>
              <Input
                value={qrCurrencyId}
                placeholder="e.g. Currency~GOLD"
                onChange={(e) => setQrCurrencyId(e.target.value)}
                style={{ width: 240 }}
                disabled={isFormDisabled}
              />
            </Col>
            <Col>
              <Title level={5}>Player Ids File</Title>
              <Upload
                accept=".txt,.json"
                maxCount={1}
                fileList={[]}
                beforeUpload={handleQueryResourceFileSelect}
                disabled={isFormDisabled}
              >
                <Button icon={<UploadOutlined />} disabled={isFormDisabled}>
                  {qrFileName ? 'Replace file' : 'Upload .txt or .json'}
                </Button>
              </Upload>
              {qrFileName && (
                <div className="mt-1">
                  <Tag closable={!isFormDisabled} onClose={handleRemoveQueryResourceFile}>
                    {qrFileName} - {qrPlayerIds.length} unique id(s)
                  </Tag>
                </div>
              )}
            </Col>
          </Row>
          <Row className="mb-4">
            <Col flex="auto">
              <Text type="secondary">
                For every uploaded player id, looks up the item in the table above where PlayerId =
                the player id and ItemId = the currency id (Amount is read as its value). Txt files:
                comma/semicolon/newline-separated ids. Json files: an array of items, each read by
                its &quot;id&quot;/&quot;Id&quot;/&quot;PlayerId&quot; field (so a file from the
                Scan tab&apos;s own Download button works too).
              </Text>
            </Col>
          </Row>
          <Row gutter={16} className="mb-4">
            <Col>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={isQueryingResource}
                disabled={!canSubmitQueryResource}
                onClick={runQueryResource}
              >
                Query Resource
              </Button>
            </Col>
          </Row>

          {jobRunningAlert}

          {qrSummary && (
            <>
              <Alert
                className="mb-2"
                type="info"
                showIcon
                message={`Queried ${qrSummary.scannedCount} player id(s) in "${qrSummary.table}", found a matching item for ${qrSummary.count}.`}
                action={(
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={isDownloading}
                    disabled={isJobRunning}
                    onClick={handleDownloadQueryResource}
                  >
                    Download .json
                  </Button>
                )}
              />
              <Text type="secondary">
                Showing a preview of the first {qrPreviewRecords.length} queried player id(s) -
                download the file above for the full result.
              </Text>
              <Table
                className="mt-2"
                rowKey="Id"
                columns={qrColumns}
                dataSource={qrPreviewRecords}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true }}
              />
            </>
          )}
        </Tabs.TabPane>

        <Tabs.TabPane tab="History" key="history">
          <Table
            rowKey="id"
            loading={historyLoading}
            columns={historyColumns}
            dataSource={historyItems}
            size="small"
            pagination={{
              current: historyPage,
              pageSize: HISTORY_PAGE_SIZE,
              total: historyTotal,
              onChange: fetchHistory,
            }}
            expandable={{
              expandedRowRender: (item: ScanHistoryItemDto) => {
                const matchedPlayerIds = item.playerIds ?? [];
                const previewPlayerIds = matchedPlayerIds.slice(0, HISTORY_PLAYER_ID_PREVIEW_SIZE);

                return (
                  <div className="flex flex-col gap-2">
                    {item.errorMessage && (
                      <Alert type="error" showIcon message={item.errorMessage} />
                    )}
                    <Text type="secondary">Params</Text>
                    <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap break-all bg-gray-50 p-2 rounded text-xs">
                      {item.params ? JSON.stringify(item.params, null, 2) : 'Not recorded'}
                    </pre>
                    {item.kind !== 'scan-all' && matchedPlayerIds.length > 0 && (
                      <>
                        <Text type="secondary">
                          Matched Player Ids ({matchedPlayerIds.length}
                          {matchedPlayerIds.length > HISTORY_PLAYER_ID_PREVIEW_SIZE
                            ? `, showing first ${HISTORY_PLAYER_ID_PREVIEW_SIZE} - download for the full list`
                            : ''}
                          )
                        </Text>
                        <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-all bg-gray-50 p-2 rounded text-xs">
                          {previewPlayerIds.join(', ')}
                        </div>
                      </>
                    )}
                    {item.kind !== 'scan-all' && item.records && item.records.length > 0 && (
                      <>
                        <Text type="secondary">
                          Matched Records (JSON, showing first{' '}
                          {Math.min(item.records.length, HISTORY_PLAYER_ID_PREVIEW_SIZE)} of{' '}
                          {item.records.length})
                        </Text>
                        <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap break-all bg-gray-50 p-2 rounded text-xs">
                          {JSON.stringify(
                            item.records.slice(0, HISTORY_PLAYER_ID_PREVIEW_SIZE),
                            null,
                            2,
                          )}
                        </pre>
                      </>
                    )}
                  </div>
                );
              },
            }}
          />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default ScanPlayersScreen;
